import { awardEngineFor } from '../award-engines';

/** Effective-dated payroll facts only. Missing facts stay visible as holds. */
export type TerminationType =
  | 'RESIGNATION'
  | 'DISMISSAL'
  | 'REDUNDANCY'
  | 'DEATH'
  | 'FIXED_TERM_END'
  | 'OTHER';
export type TerminationLineCategory =
  | 'WAGES_TO_LAST_DAY'
  | 'UNUSED_ANNUAL_LEAVE'
  | 'ANNUAL_LEAVE_LOADING'
  | 'SHIFTWORKER_ADDITIONAL_LEAVE'
  | 'OUTSTANDING_TOIL'
  | 'PERSONAL_LEAVE'
  | 'EMPLOYEE_NOTICE'
  | 'LONG_SERVICE_LEAVE'
  | 'REDUNDANCY'
  | 'PAYMENT_IN_LIEU_OF_NOTICE'
  | 'NOTICE_SHORTFALL_DEDUCTION'
  | 'FINAL_PAY_DUE_DATE'
  | 'WAITING_TIME_EXPOSURE'
  | 'DEATH_BENEFITS'
  | 'TAX_ETP';
export type TerminationLine = {
  category: TerminationLineCategory;
  status: 'PAYABLE' | 'ZERO' | 'HOLD';
  hours?: number;
  amount?: number;
  rateBasis: string;
  citations: string[];
  reason: string;
  holdCode?: string;
};

export type TerminationEntitlementsInput = {
  instrumentId?: string | null;
  /** Exact FWC approval version, confirmed current for terminationDate by payroll/legal. */
  verifiedInstrumentVersion?: string | null;
  terminationType: TerminationType;
  terminationDate: string;
  employmentStartDate?: string | null;
  employmentBasis?: 'FULL_TIME' | 'PART_TIME' | 'CASUAL' | 'UNKNOWN';
  annualLeaveHours?: number | null;
  annualLeaveOrdinaryRate?: number | null;
  projectedAnnualLeavePenaltyAmount?: number | null;
  employeeCategory?:
    | 'DAYWORKER'
    | 'SHIFTWORKER'
    | 'PERMANENT_NIGHT'
    | 'ROTATING_ROSTER'
    | 'OTHER'
    | null;
  shiftworkerAdditionalLeaveHours?: number | null;
  shiftworkerEligibility?: boolean | null;
  outstandingToilHours?: number | null;
  overtimeRate?: number | null;
  longServiceLeaveHours?: number | null;
  ordinaryHourlyRate?: number | null;
  ordinaryWeeklyHours?: number | null;
  dismissalForSeriousWilfulMisconduct?: boolean | null;
  lslGround?: 'RETIREMENT_AGE' | 'ILLNESS' | 'INCAPACITY_OR_PRESSING_NECESSITY' | 'OTHER' | null;
  lslYearsSinceLastAccrual?: number | null;
  lslRedundancyOverlayResolved?: boolean | null;
  redundancyVoluntary?: boolean | null;
  employerInitiated?: boolean | null;
  noticePayAmount?: number | null;
  noticeRequiredDays?: number | null;
  noticeGivenDays?: number | null;
  noticeGivenDate?: string | null;
  employerConsentedShortNotice?: boolean | null;
  redundancyWeeksPerYear?: number | null;
  redundancyYears?: number | null;
  redundancyWeeklyPay?: number | null;
  nesRedundancyAmount?: number | null;
  projectedWeeklyShiftPay?: number | null;
  finalPayDueDate?: string | null;
  hourlyWaitingRate?: number | null;
  daysLate?: number | null;
  wagesReference?: string | null;
};

/**
 * Primary-source verification: docs/operations/clause-verifications/2026-09-28-termination-final-pay-entitlements.md
 * and docs/operations/clause-verifications/2026-09-28-termination-final-pay-entitlements-nurses-ea.md.
 */
const SOURCES = {
  fw: 'Fair Work Act 2009 (Cth) ss 90(2), 95, 99, 117, 118, 119, 121, 123, 323, 324',
  gs: 'Medea General Staff Enterprise Agreement 2024 (AE529357) cls 14, 28, 39, 47',
  nurses:
    'Medea Nurses Enterprise Agreement 2024 (AE530670) cls 17.5–17.9, 19.13–19.15, 21.10, 23.14–23.15, 35',
  tas: 'Long Service Leave Act 1976 (Tas) ss 8, 9, 11, 12(4)',
  award:
    'Aged Care Award 2010 (MA000018) cl 11.1(d) (short-notice award term displaced by NES s 118 for EAs); cl 28 termination-loading text not retrieved, use FW Act s 90(2)',
} as const;

const VERIFIED_VERSIONS: Record<
  string,
  { version: string; effectiveFrom: string; awardCode: string; nurse: boolean; citations: string[] }
> = {
  AE529357: {
    version: 'PR788182',
    effectiveFrom: '2025-06-20',
    awardCode: 'MA000018',
    nurse: false,
    citations: [SOURCES.fw, SOURCES.gs],
  },
  AE530670: {
    version: 'PR792386',
    effectiveFrom: '2025-10-13',
    awardCode: 'MA000034',
    nurse: true,
    citations: [SOURCES.fw, SOURCES.nurses],
  },
};
const hold = (
  category: TerminationLineCategory,
  reason: string,
  holdCode: string,
  citations: string[],
): TerminationLine => ({
  category,
  status: 'HOLD',
  rateBasis: 'Provider determination required',
  citations,
  reason,
  holdCode,
});
const zero = (
  category: TerminationLineCategory,
  reason: string,
  citations: string[],
): TerminationLine => ({
  category,
  status: 'ZERO',
  amount: 0,
  rateBasis: 'Not payable',
  citations,
  reason,
});
const payable = (
  category: TerminationLineCategory,
  amount: number,
  citations: string[],
  reason: string,
  hours?: number,
): TerminationLine =>
  Number.isFinite(amount)
    ? {
        category,
        status: amount === 0 ? 'ZERO' : 'PAYABLE',
        amount: Number(amount.toFixed(2)),
        hours,
        rateBasis: 'Effective payroll rate and reconciled ledger',
        citations,
        reason,
      }
    : hold(
        category,
        'The supplied facts produce an invalid amount; reconcile the rate and balance.',
        'INVALID_AMOUNT',
        citations,
      );
const finite = (n: number | null | undefined): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n >= 0;

/**
 * Source-backed projection, never a certificate or payroll posting.
 * Approval IDs bind the reviewed 2024 EA texts; a pay-role ID alone is insufficient.
 * The caller must establish current coverage/version before supplying verifiedInstrumentVersion.
 * D-F pending rules never consume caller-supplied monetary guesses.
 */
export function terminationEntitlementsFor(input: TerminationEntitlementsInput): TerminationLine[] {
  const candidate = input.instrumentId ? VERIFIED_VERSIONS[input.instrumentId] : undefined;
  const rule =
    candidate &&
    input.verifiedInstrumentVersion === candidate.version &&
    /^\d{4}-\d{2}-\d{2}$/.test(input.terminationDate) &&
    Number.isFinite(Date.parse(`${input.terminationDate}T00:00:00Z`)) &&
    input.terminationDate >= candidate.effectiveFrom
      ? candidate
      : undefined;
  const citations = rule?.citations ?? [SOURCES.fw];
  if (rule) awardEngineFor(rule.awardCode);
  const lines: TerminationLine[] = [
    hold(
      'WAGES_TO_LAST_DAY',
      'Reconcile approved and unapproved timesheets through the final date, including work after the last day worked.',
      'TIMESHEETS_REQUIRED',
      citations,
    ),
  ];
  const monetaryHold = !rule
    ? 'Confirm the current effective instrument and its verified FWC approval version; the assignment alone does not verify coverage.'
    : input.terminationType === 'DEATH'
      ? 'Payroll must verify the estate payee and death settlement authority before releasing money.'
      : !['FULL_TIME', 'PART_TIME', 'CASUAL'].includes(input.employmentBasis ?? '')
        ? 'Confirm full-time, part-time or casual employment from effective payroll evidence.'
        : null;
  const holdCode = !rule
    ? 'UNVERIFIED_INSTRUMENT_VERSION'
    : input.terminationType === 'DEATH'
      ? 'DEATH_SETTLEMENT_AUTHORITY_REQUIRED'
      : 'EMPLOYMENT_BASIS_REQUIRED';
  if (monetaryHold) {
    for (const category of [
      'UNUSED_ANNUAL_LEAVE',
      'ANNUAL_LEAVE_LOADING',
      'SHIFTWORKER_ADDITIONAL_LEAVE',
      'OUTSTANDING_TOIL',
    ] as const)
      lines.push(hold(category, monetaryHold, holdCode, citations));
  } else if (input.employmentBasis === 'CASUAL') {
    const conflict = finite(input.annualLeaveHours) && input.annualLeaveHours > 0;
    for (const category of [
      'UNUSED_ANNUAL_LEAVE',
      'ANNUAL_LEAVE_LOADING',
      'SHIFTWORKER_ADDITIONAL_LEAVE',
    ] as const)
      lines.push(
        conflict
          ? hold(
              category,
              'Casual employment conflicts with a positive leave balance; reconcile classification and ledger.',
              'CASUAL_LEAVE_BALANCE_CONFLICT',
              citations,
            )
          : zero(
              category,
              'Verified casual employment does not accrue paid annual leave.',
              citations,
            ),
      );
    lines.push(
      hold(
        'OUTSTANDING_TOIL',
        'Confirm any outstanding TOIL and the applicable instrument treatment.',
        'TOIL_REVIEW_REQUIRED',
        citations,
      ),
    );
  } else {
    const validAnnual =
      finite(input.annualLeaveHours) &&
      finite(input.annualLeaveOrdinaryRate) &&
      (input.annualLeaveHours === 0 || input.annualLeaveOrdinaryRate > 0);
    lines.push(
      validAnnual
        ? payable(
            'UNUSED_ANNUAL_LEAVE',
            input.annualLeaveHours! * input.annualLeaveOrdinaryRate!,
            citations,
            'Verified untaken annual leave at the effective ordinary rate.',
            input.annualLeaveHours!,
          )
        : hold(
            'UNUSED_ANNUAL_LEAVE',
            'Confirm the current reconciled annual leave ledger and positive effective rate.',
            'LEAVE_BALANCE_OR_RATE_REQUIRED',
            citations,
          ),
    );
    if (
      validAnnual &&
      (!rule!.nurse || ['DAYWORKER', 'SHIFTWORKER'].includes(input.employeeCategory ?? '')) &&
      ((rule!.nurse && input.employeeCategory === 'DAYWORKER') ||
        finite(input.projectedAnnualLeavePenaltyAmount))
    ) {
      const floor = input.annualLeaveHours! * input.annualLeaveOrdinaryRate! * 0.175;
      const amount =
        rule!.nurse && input.employeeCategory === 'DAYWORKER'
          ? floor
          : Math.max(floor, input.projectedAnnualLeavePenaltyAmount!);
      lines.push(
        payable(
          'ANNUAL_LEAVE_LOADING',
          amount,
          citations,
          'Verified annual leave loading with the applicable roster comparison.',
        ),
      );
    } else
      lines.push(
        hold(
          'ANNUAL_LEAVE_LOADING',
          'Confirm annual leave facts, employee category and the projected roster penalty comparison; no loading estimate is released.',
          'PROJECTED_ROSTER_COMPARATOR_REQUIRED',
          citations,
        ),
      );
    lines.push(
      input.shiftworkerEligibility === false
        ? zero(
            'SHIFTWORKER_ADDITIONAL_LEAVE',
            'Provider confirmed the additional-leave criteria are not met.',
            citations,
          )
        : hold(
            'SHIFTWORKER_ADDITIONAL_LEAVE',
            'Reconcile shiftworker eligibility and ensure additional hours are not already included in the annual leave ledger.',
            'SHIFTWORKER_LEDGER_REVIEW_REQUIRED',
            citations,
          ),
    );
    lines.push(
      rule!.nurse &&
        finite(input.outstandingToilHours) &&
        finite(input.overtimeRate) &&
        (input.outstandingToilHours === 0 || input.overtimeRate > 0)
        ? payable(
            'OUTSTANDING_TOIL',
            input.outstandingToilHours * input.overtimeRate,
            citations,
            'Verified Nurses EA outstanding TOIL at the overtime rate.',
            input.outstandingToilHours,
          )
        : hold(
            'OUTSTANDING_TOIL',
            'Confirm outstanding TOIL hours and instrument treatment; no absence of entitlement is inferred.',
            'TOIL_REVIEW_REQUIRED',
            citations,
          ),
    );
  }
  // D-F: complete primary-source, service, eligibility and provider settlement rules
  // are not approved for these outputs. Never turn supplied amounts into entitlements.
  const pending: Array<[TerminationLineCategory, string]> = [
    [
      'PERSONAL_LEAVE',
      'Payroll must verify any instrument-specific personal/carer’s leave termination entitlement.',
    ],
    [
      'EMPLOYEE_NOTICE',
      'Verify continuous service, notice dates and the current instrument notice rule.',
    ],
    [
      'LONG_SERVICE_LEAVE',
      'Provider must approve Tasmania LSL authority, continuous service, portability and the termination ground.',
    ],
    [
      'REDUNDANCY',
      'Verify redundancy eligibility, exclusions, continuous service and the agreement/NES comparison.',
    ],
    [
      'PAYMENT_IN_LIEU_OF_NOTICE',
      'Verify employer notice eligibility, full-rate pay and the notice actually worked.',
    ],
    [
      'NOTICE_SHORTFALL_DEDUCTION',
      'No automatic deduction is authorised; payroll must review the current instrument and legal authority.',
    ],
    [
      'DEATH_BENEFITS',
      'Verify whether death benefits apply and any estate payee or settlement authority.',
    ],
    [
      'TAX_ETP',
      'Payroll must verify tax and employment termination payment treatment with the provider.',
    ],
  ];
  for (const [category, reason] of pending)
    lines.push(hold(category, reason, 'PROVIDER_RULE_APPROVAL_REQUIRED', citations));
  if (rule?.nurse) {
    lines.push(
      hold(
        'FINAL_PAY_DUE_DATE',
        'Confirm the termination day and provider pay-office working calendar.',
        'PAY_OFFICE_CALENDAR_REQUIRED',
        citations,
      ),
    );
    lines.push(
      hold(
        'WAITING_TIME_EXPOSURE',
        'Payroll must verify actual delay and applicable waiting-time liability.',
        'FINAL_PAY_DELAY_FACTS_REQUIRED',
        citations,
      ),
    );
  }
  return lines;
}
